// react
import { useCallback, useEffect } from 'react';

// feature-hooks
import { useAnalysisPreferences } from '@/features/energyxplorer/hooks/useAnalysisPreferences';

// components
import useSearchParams from '@/hooks/useStableSearchParams';

const VALID = new Set(['sum', 'mean', 'min', 'max']);

export function useAggregationParam(
  key = 'aggregation',
  fallbackValue = 'mean',
) {
  const [sp, setSp] = useSearchParams();
  const { preferences, setPreference } = useAnalysisPreferences();

  // Default to the user's last-used aggregation (persisted in localStorage),
  // falling back to fallbackValue if nothing has been persisted yet.
  const defaultValue = VALID.has(preferences?.aggregation)
    ? preferences.aggregation
    : fallbackValue;

  const raw = sp.get(key);
  const aggregation = VALID.has(raw) ? raw : defaultValue;

  // Ensure default is present in URL if missing or invalid
  useEffect(() => {
    setSp(
      (prev) => {
        const qp = new URLSearchParams(prev);
        const raw = qp.get(key);
        if (VALID.has(raw)) return prev;

        qp.set(key, defaultValue);
        return qp;
      },
      { replace: true },
    );
  }, [key, defaultValue, setSp]);

  const setAggregation = useCallback(
    (next, { push = false } = {}) => {
      if (!VALID.has(next)) return;

      setSp(
        (prev) => {
          const qp = new URLSearchParams(prev);
          qp.set(key, next);
          return qp;
        },
        { replace: !push },
      );

      // Persist choice so future analyses default to it instead of 'mean'
      setPreference('aggregation', next);
    },
    [key, setSp, setPreference],
  );

  const clearAggregation = useCallback(() => {
    setSp(
      (prev) => {
        const qp = new URLSearchParams(prev);
        qp.delete(key);
        return qp;
      },
      { replace: true },
    );
  }, [key, setSp]);

  return { aggregation, setAggregation, clearAggregation };
}
